import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Smartphone } from 'lucide-react';
import { Button, Spinner } from '@brimostech/ui';
import { residentOnly } from '@/lib/permissions';
import { useAuth, useCurrentUser } from './auth-context';

export const MFA_SETUP_PATH = '/seguranca/2fa';

export function FullScreenLoader() {
  return (
    <div className="flex min-h-svh items-center justify-center text-muted-foreground">
      <Spinner className="size-6" />
      <span className="sr-only">Carregando</span>
    </div>
  );
}

/** Route guard: authenticated users only; operators/administrators are sent to MFA setup first. */
export function RequireAuth() {
  const { status, user } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <FullScreenLoader />;
  if (status === 'anonymous' || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (user.mfaSetupRequired && location.pathname !== MFA_SETUP_PATH) {
    return <Navigate to={MFA_SETUP_PATH} replace />;
  }
  if (residentOnly(user)) return <ResidentNotice />;
  return <Outlet />;
}

function ResidentNotice() {
  const { user, logout } = useAuth();
  return (
    <div className="flex min-h-svh items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-4 rounded-2xl border bg-card p-6 text-center shadow-lg">
        <Smartphone className="mx-auto size-10 text-primary" />
        <h1 className="text-xl font-semibold">O portal é da administração</h1>
        <p className="text-sm text-muted-foreground">
          {user?.name ? `${user.name}, sua` : 'Sua'} conta é de morador. Abra portas, veja as
          câmeras e os avisos pelo aplicativo do condomínio no celular.
        </p>
        <Button variant="outline" onClick={() => void logout()}>
          Sair
        </Button>
      </div>
    </div>
  );
}

export function RequireSuperadmin({ children }: { children: ReactNode }) {
  const user = useCurrentUser();
  if (!user.isSuperadmin) return <Navigate to="/" replace />;
  return children;
}

export function RequirePermission({
  permission,
  children,
}: {
  permission: string;
  children: ReactNode;
}) {
  const user = useCurrentUser();
  if (
    !user.isSuperadmin &&
    !user.permissions.includes(permission as (typeof user.permissions)[number])
  ) {
    return <Navigate to="/" replace />;
  }
  return children;
}
